import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BellRing, CheckCheck, DatabaseZap, Eraser, ListChecks, MailOpen, Search, Send, X } from "lucide-react";
import { API, Button, Card, PageHeader, Toggle, apiFetch, cx, useToast } from "./_shared";
import {
  desktopNotificationsSupported,
  useAdminNotificationsContext,
} from "../../components/admin/notifications/AdminNotificationsContext";
import { useNotificationFeed } from "../../components/admin/notifications/useNotificationFeed";
import NotificationList from "../../components/admin/notifications/NotificationList";
import { GROUP_META, TYPE_META } from "../../components/admin/notifications/notificationMeta";

const STATUS_TABS = [
  { value: "all", label: "Tất cả" },
  { value: "unread", label: "Chưa đọc" },
];

function Chip({ active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition",
        active
          ? "border-admin-primary bg-admin-primaryLight text-admin-primary"
          : "border-admin-border bg-admin-surface text-admin-textMuted hover:border-admin-textMuted/40 hover:text-admin-text"
      )}
    >
      {children}
      {count != null ? <span className={cx("tabular-nums", active ? "text-admin-primary" : "text-admin-textMuted/70")}>{count}</span> : null}
    </button>
  );
}

function InboxTab() {
  const { unread } = useAdminNotificationsContext();
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const feed = useNotificationFeed({ filter: status, type, q, pageSize: 25 });
  const { push, ToastPortal } = useToast();

  const total = useMemo(() => Object.values(feed.typeCounts).reduce((a, b) => a + b, 0), [feed.typeCounts]);

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, []);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runBulk = async (action, doneMsg) => {
    const ids = [...selected];
    if (!ids.length) return;
    const res = await feed.bulk(ids, action);
    if (res) push(`${doneMsg} ${ids.length} thông báo`);
    exitSelect();
  };

  const clearRead = async () => {
    const res = await feed.clearRead();
    if (res) push(res.cleared ? `Đã ẩn ${res.cleared} thông báo đã đọc` : "Không có thông báo đã đọc để ẩn", res.cleared ? "success" : "info");
  };

  const filtering = Boolean(type || q.trim());

  const [backfilling, setBackfilling] = useState(false);
  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await apiFetch(`${API}/notifications/admin/backfill`, { method: "POST" });
      push(res.total ? `Đã tạo ${res.total} thông báo từ dữ liệu có sẵn` : "Không có thêm sự kiện nào để tạo thông báo", res.total ? "success" : "info");
      feed.reload();
    } catch (e) {
      push(e?.message || "Không tạo được thông báo", "error");
    } finally {
      setBackfilling(false);
    }
  };

  const typeCount = (t) => feed.typeCounts[t] ?? 0;
  const groupCount = (g) => Object.keys(TYPE_META).filter((t) => TYPE_META[t].group === g).reduce((n, t) => n + typeCount(t), 0);

  const navBtn = (active, onClick, children) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition",
        active ? "bg-admin-primaryLight font-semibold text-admin-primary" : "text-admin-text hover:bg-admin-muted"
      )}
    >
      {children}
    </button>
  );

  return (
    <>
      <div className="grid gap-5 lg:grid-cols-[240px,minmax(0,1fr)] xl:grid-cols-[260px,minmax(0,1fr)]">
        {/* Bộ lọc theo loại — cột trái (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 space-y-4">
            <Card className="p-2">
              {navBtn(!type, () => setType(""), (
                <>
                  <BellRing size={15} className="shrink-0" />
                  <span className="flex-1">Tất cả</span>
                  <span className="text-xs tabular-nums text-admin-textMuted">{total}</span>
                </>
              ))}
            </Card>
            {GROUP_META.map((g) => (
              <Card key={g.id} className="p-2">
                <p className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">{g.label}</p>
                {Object.keys(TYPE_META)
                  .filter((t) => TYPE_META[t].group === g.id)
                  .map((t) => {
                    const Icon = TYPE_META[t].icon;
                    return (
                      <React.Fragment key={t}>
                        {navBtn(type === t, () => setType(type === t ? "" : t), (
                          <>
                            <Icon size={15} className="shrink-0" />
                            <span className="min-w-0 flex-1 truncate">{TYPE_META[t].label}</span>
                            <span className="text-xs tabular-nums text-admin-textMuted">{typeCount(t)}</span>
                          </>
                        ))}
                      </React.Fragment>
                    );
                  })}
              </Card>
            ))}
          </div>
        </aside>

        <div className="min-w-0">
          {/* Lọc theo loại — thanh chip cuộn ngang (mobile / tablet) */}
          <div className="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1 lg:hidden" role="group" aria-label="Lọc theo loại">
            <Chip active={!type} onClick={() => setType("")} count={total}>
              Tất cả
            </Chip>
            {GROUP_META.map((g) => (
              <Chip key={g.id} active={Boolean(type) && TYPE_META[type]?.group === g.id} onClick={() => setType(Object.keys(TYPE_META).find((t) => TYPE_META[t].group === g.id && typeCount(t) > 0) || "")} count={groupCount(g.id)}>
                {g.label}
              </Chip>
            ))}
          </div>

      <Card>
        {/* Thanh công cụ */}
        <div className="space-y-3 border-b border-admin-border p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-9 min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-admin-border bg-admin-surface px-3 transition focus-within:border-admin-primary focus-within:ring-4 focus-within:ring-admin-primary/10">
              <Search size={15} className="shrink-0 text-admin-textMuted" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Tìm trong thông báo…"
                aria-label="Tìm trong thông báo"
                className="min-w-0 flex-1 bg-transparent text-sm text-admin-text outline-none placeholder:text-admin-textMuted/60"
              />
              {q ? (
                <button type="button" aria-label="Xoá tìm kiếm" onClick={() => setQ("")} className="text-admin-textMuted hover:text-admin-text">
                  <X size={14} />
                </button>
              ) : null}
            </div>

            <div role="tablist" className="inline-flex items-center gap-0.5 rounded-lg bg-admin-muted p-0.5">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={status === t.value}
                  onClick={() => setStatus(t.value)}
                  className={cx(
                    "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                    status === t.value ? "bg-admin-surface text-admin-text shadow-sm" : "text-admin-textMuted hover:text-admin-text"
                  )}
                >
                  {t.label}
                  {t.value === "unread" && unread > 0 ? <span className="ml-1.5 rounded-full bg-admin-primary px-1.5 text-[10px] leading-4 text-white">{unread}</span> : null}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1">
              <Button size="sm" variant={selectMode ? "soft" : "ghost"} icon={ListChecks} onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}>
                {selectMode ? "Xong" : "Chọn"}
              </Button>
              <Button size="sm" variant="ghost" icon={Eraser} onClick={clearRead} title="Ẩn tất cả thông báo đã đọc">
                Dọn đã đọc
              </Button>
            </div>
          </div>

          {type ? (
            <p className="flex items-center gap-2 text-xs text-admin-textMuted">
              Đang lọc:
              <span className="inline-flex items-center gap-1 rounded-full bg-admin-primaryLight px-2 py-0.5 font-medium text-admin-primary">
                {TYPE_META[type]?.label || type}
                <button type="button" onClick={() => setType("")} aria-label="Bỏ lọc loại" className="hover:text-admin-text">
                  <X size={12} />
                </button>
              </span>
            </p>
          ) : null}
        </div>

        {/* Thanh thao tác hàng loạt */}
        {selectMode ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-admin-border bg-admin-primaryLight/50 px-4 py-2.5">
            <p className="text-sm text-admin-text">
              Đã chọn <span className="font-semibold">{selected.size}</span> thông báo
              <button type="button" className="ml-3 text-xs font-semibold text-admin-primary hover:underline" onClick={() => setSelected(new Set(feed.items.map((i) => i._id)))}>
                Chọn tất cả ({feed.items.length})
              </button>
            </p>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="secondary" icon={CheckCheck} disabled={!selected.size} onClick={() => runBulk("read", "Đã đọc")}>
                Đã đọc
              </Button>
              <Button size="sm" variant="secondary" icon={MailOpen} disabled={!selected.size} onClick={() => runBulk("unread", "Đã đánh dấu chưa đọc")}>
                Chưa đọc
              </Button>
              <Button size="sm" variant="secondary" icon={X} disabled={!selected.size} onClick={() => runBulk("dismiss", "Đã ẩn")}>
                Ẩn
              </Button>
            </div>
          </div>
        ) : null}

        <NotificationList
          feed={feed}
          selectMode={selectMode}
          selected={selected}
          onSelect={toggle}
          emptyAction={
            !filtering && status === "all" ? (
              <Button size="sm" variant="secondary" icon={DatabaseZap} loading={backfilling} onClick={backfill}>
                Tạo từ dữ liệu có sẵn
              </Button>
            ) : null
          }
          emptyHint={
            filtering
              ? "Không có thông báo nào khớp bộ lọc."
              : status === "unread"
                ? "Bạn đã đọc hết thông báo."
                : undefined
          }
        />
      </Card>
        </div>
      </div>
      <ToastPortal />
    </>
  );
}

function SettingsTab() {
  const { desktopEnabled, setDesktopEnabled, setUnread } = useAdminNotificationsContext();
  const { push, ToastPortal } = useToast();
  const [types, setTypes] = useState(null);
  const [testType, setTestType] = useState("system");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const supported = desktopNotificationsSupported();
  const denied = supported && typeof Notification !== "undefined" && Notification.permission === "denied";

  useEffect(() => {
    let alive = true;
    apiFetch(`${API}/notifications/admin/preferences`)
      .then((r) => alive && setTypes(r.types))
      .catch((e) => alive && setError(e?.message || "Không tải được cài đặt"));
    return () => {
      alive = false;
    };
  }, []);

  const toggleType = async (type, enabled) => {
    const prev = types;
    const next = types.map((t) => (t.type === type ? { ...t, enabled } : t));
    setTypes(next);
    try {
      const res = await apiFetch(`${API}/notifications/admin/preferences`, {
        method: "PUT",
        body: JSON.stringify({ mutedTypes: next.filter((t) => !t.enabled).map((t) => t.type) }),
      });
      setTypes(res.types);
      setUnread(res.unread);
    } catch (e) {
      setTypes(prev);
      push(e?.message || "Không lưu được cài đặt", "error");
    }
  };

  const toggleDesktop = async (on) => {
    const ok = await setDesktopEnabled(on);
    if (on && !ok) push("Trình duyệt chưa cho phép hiện thông báo. Hãy cấp quyền trong cài đặt trang web.", "error");
    else push(on ? "Đã bật thông báo trên trình duyệt" : "Đã tắt thông báo trên trình duyệt");
  };

  const backfillSettings = async () => {
    setBusy("backfill");
    try {
      const res = await apiFetch(`${API}/notifications/admin/backfill`, { method: "POST" });
      setUnread(res.unread);
      push(res.total ? `Đã tạo ${res.total} thông báo từ dữ liệu có sẵn` : "Không có thêm sự kiện nào để tạo thông báo", res.total ? "success" : "info");
    } catch (e) {
      push(e?.message || "Không tạo được thông báo", "error");
    } finally {
      setBusy("");
    }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      await apiFetch(`${API}/notifications/admin/test`, { method: "POST", body: JSON.stringify({ type: testType, link: "/admin/notifications", title: `Thông báo thử — ${TYPE_META[testType]?.label || "Hệ thống"}` }) });
      push("Đã gửi thông báo thử — chuông sẽ cập nhật trong giây lát");
    } catch (e) {
      push(e?.message || "Không gửi được", "error");
    } finally {
      setBusy("");
    }
  };

  const mutedCount = types ? types.filter((t) => !t.enabled).length : 0;
  const setGroup = async (ids, enabled) => {
    if (!types) return;
    const prev = types;
    const next = types.map((t) => (ids.includes(t.type) ? { ...t, enabled } : t));
    setTypes(next);
    try {
      const res = await apiFetch(`${API}/notifications/admin/preferences`, {
        method: "PUT",
        body: JSON.stringify({ mutedTypes: next.filter((t) => !t.enabled).map((t) => t.type) }),
      });
      setTypes(res.types);
      setUnread(res.unread);
    } catch (e) {
      setTypes(prev);
      push(e?.message || "Không lưu được cài đặt", "error");
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
      <div className="space-y-6">
        {error ? (
          <Card>
            <p className="px-5 py-8 text-center text-sm text-rose-600">{error}</p>
          </Card>
        ) : !types ? (
          <Card>
            <p className="px-5 py-8 text-center text-sm text-admin-textMuted">Đang tải…</p>
          </Card>
        ) : (
          GROUP_META.map((g) => {
            const list = types.filter((t) => (TYPE_META[t.type]?.group || t.group) === g.id);
            if (!list.length) return null;
            const ids = list.map((t) => t.type);
            const allOn = list.every((t) => t.enabled);
            return (
              <Card key={g.id}>
                <div className="flex items-center justify-between gap-3 border-b border-admin-border px-5 py-3.5">
                  <h2 className="text-[15px] font-semibold text-admin-text">{g.label}</h2>
                  <button type="button" onClick={() => setGroup(ids, !allOn)} className="text-xs font-semibold text-admin-primary hover:underline">
                    {allOn ? "Tắt cả nhóm" : "Bật cả nhóm"}
                  </button>
                </div>
                <ul className="divide-y divide-admin-border">
                  {list.map((t) => {
                    const Icon = TYPE_META[t.type]?.icon || BellRing;
                    return (
                      <li key={t.type} className="flex items-center justify-between gap-4 px-5 py-3.5">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-admin-muted text-admin-textMuted">
                            <Icon size={16} />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-admin-text">{t.label}</p>
                            <p className="text-xs text-admin-textMuted">{t.description}</p>
                          </div>
                        </div>
                        <Toggle checked={t.enabled} onChange={(e) => toggleType(t.type, e.target.checked)} labelOn="Bật" labelOff="Tắt" />
                      </li>
                    );
                  })}
                </ul>
              </Card>
            );
          })
        )}
      </div>

      <div className="space-y-6 xl:sticky xl:top-20 xl:self-start">
        <Card>
          <div className="px-5 py-4">
            <h2 className="text-[15px] font-semibold text-admin-text">Tóm tắt</h2>
            <p className="mt-1 text-xs text-admin-textMuted">
              {types ? `Đang nhận ${types.length - mutedCount}/${types.length} loại thông báo.` : "Đang tải…"} Cài đặt áp dụng riêng cho tài khoản của bạn.
            </p>
          </div>
        </Card>

        <Card>
          <div className="space-y-3 px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-[15px] font-semibold text-admin-text">Thông báo trên trình duyệt</h2>
                <p className="mt-1 text-xs leading-relaxed text-admin-textMuted">
                  {!supported
                    ? "Trình duyệt này không hỗ trợ thông báo."
                    : denied
                      ? "Bạn đã chặn thông báo cho trang này — hãy cho phép lại trong cài đặt trang web của trình duyệt."
                      : "Hiện cửa sổ thông báo của hệ điều hành khi có thông báo mới mà bạn đang ở tab khác."}
                </p>
              </div>
              <Toggle checked={desktopEnabled && supported && !denied} onChange={(e) => supported && !denied && toggleDesktop(e.target.checked)} labelOn="Bật" labelOff="Tắt" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="space-y-3 px-5 py-4">
            <div>
              <h2 className="text-[15px] font-semibold text-admin-text">Tạo từ dữ liệu có sẵn</h2>
              <p className="mt-1 text-xs leading-relaxed text-admin-textMuted">
                Quét 90 ngày gần nhất (người dùng đăng ký, mốc lượt xem, yêu thích, đột biến truy cập, thay đổi quyền, cài đặt) và tạo thông báo đúng thời điểm xảy ra. Bấm nhiều lần cũng không bị trùng.
              </p>
            </div>
            <Button variant="secondary" icon={DatabaseZap} onClick={backfillSettings} loading={busy === "backfill"} className="w-full">
              Tạo thông báo
            </Button>
          </div>
        </Card>

        <Card>
          <div className="space-y-3 px-5 py-4">
            <div>
              <h2 className="text-[15px] font-semibold text-admin-text">Gửi thông báo thử</h2>
              <p className="mt-1 text-xs leading-relaxed text-admin-textMuted">Tạo một thông báo mẫu để xem chuông, huy hiệu và biểu tượng của từng loại.</p>
            </div>
            <select
              value={testType}
              onChange={(e) => setTestType(e.target.value)}
              aria-label="Loại thông báo thử"
              className="h-9 w-full rounded-lg border border-admin-border bg-admin-surface px-3 text-sm text-admin-text outline-none focus:border-admin-primary focus:ring-4 focus:ring-admin-primary/10"
            >
              {Object.keys(TYPE_META).map((t) => (
                <option key={t} value={t}>
                  {TYPE_META[t].label}
                </option>
              ))}
            </select>
            <Button variant="secondary" icon={Send} onClick={sendTest} loading={busy === "test"} className="w-full">
              Gửi thử
            </Button>
          </div>
        </Card>
      </div>
      <ToastPortal />
    </div>
  );
}

export default function AdminNotifications() {
  const { unread } = useAdminNotificationsContext();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "settings" ? "settings" : "inbox";
  const setTab = (t) => setParams(t === "inbox" ? {} : { tab: t }, { replace: true });

  return (
    <div className="w-full">
      <PageHeader
        title="Thông báo"
        subtitle={unread > 0 ? `${unread} thông báo chưa đọc` : "Bạn đã đọc hết thông báo"}
      />
      <div role="tablist" className="mb-5 flex items-center gap-6 border-b border-admin-border">
        {[
          { id: "inbox", label: "Hộp thư" },
          { id: "settings", label: "Cài đặt" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cx(
              "-mb-px border-b-2 px-0.5 pb-3 text-sm font-medium transition",
              tab === t.id ? "border-admin-primary text-admin-text" : "border-transparent text-admin-textMuted hover:text-admin-text"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "inbox" ? <InboxTab /> : <SettingsTab />}
    </div>
  );
}
